import React, { createContext, useContext, useState } from 'react';

const CarritoContext = createContext();

export const useCarrito = () => {
  const context = useContext(CarritoContext);
  if (!context) {
    throw new Error('useCarrito debe ser usado dentro de CarritoProvider');
  }
  return context;
};

export const CarritoProvider = ({ children }) => {
  const [carrito, setCarrito] = useState([]);

  /**
   * Agrega un producto al carrito.
   * Si se proporciona un lineId se trata como línea única (permite mismo producto con distinto descuento).
   * Si no se proporciona lineId, se intentará fusionar por id y ausencia de descuento.
   */
  const agregarAlCarrito = (producto, cantidad = 1) => {
    setCarrito(prevCarrito => {
      const { lineId, id, descuentoPorcentaje } = producto;
      if (lineId) {
        // Línea independiente siempre
        return [...prevCarrito, { ...producto, cantidad }];
      }
      // Fusionar sólo si no hay descuento y existe misma id sin descuento
      const existente = prevCarrito.find(item => item.id === id && !item.descuentoPorcentaje);
      if (existente && !descuentoPorcentaje) {
        return prevCarrito.map(item =>
          item.id === id && !item.descuentoPorcentaje
            ? { ...item, cantidad: item.cantidad + cantidad }
            : item
        );
      }
      return [...prevCarrito, { ...producto, cantidad }];
    });
  };

  const eliminarDelCarrito = (productoId) => {
    setCarrito(prevCarrito => prevCarrito.filter(item => item.id !== productoId));
  };

  const eliminarLinea = (lineId) => {
    setCarrito(prev => prev.filter(item => item.lineId !== lineId));
  };

  const actualizarCantidad = (productoId, nuevaCantidad) => {
    if (nuevaCantidad <= 0) {
      eliminarDelCarrito(productoId);
      return;
    }
    
    setCarrito(prevCarrito =>
      prevCarrito.map(item =>
        item.id === productoId
          ? { ...item, cantidad: nuevaCantidad }
          : item
      )
    );
  };

  const vaciarCarrito = () => {
    setCarrito([]);
  };

  const obtenerTotal = () => {
    return carrito.reduce((total, item) => total + (item.precio * item.cantidad), 0);
  };

  const obtenerSubtotalSinDescuento = () => {
    return carrito.reduce((total, item) => {
      const base = item.precioOriginal != null ? item.precioOriginal : item.precio;
      return total + base * item.cantidad;
    }, 0);
  };

  const obtenerTotalDescuento = () => {
    return carrito.reduce((acc, item) => {
      if (item.precioOriginal != null && item.precioOriginal > item.precio) {
        return acc + (item.precioOriginal - item.precio) * item.cantidad;
      }
      return acc;
    }, 0);
  };

  const calcularIVA = (tasa = 0.16) => {
    const total = obtenerTotal();
    return total * tasa;
  };

  const obtenerTotalConIVA = (tasa = 0.16) => {
    const total = obtenerTotal();
    return total + (total * tasa);
  };

  const obtenerCantidadTotal = () => {
    return carrito.reduce((total, item) => total + item.cantidad, 0);
  };

  const value = {
    carrito,
    setCarrito,
    agregarAlCarrito,
    eliminarDelCarrito,
    actualizarCantidad,
    vaciarCarrito,
    obtenerTotal,
    obtenerSubtotalSinDescuento,
    obtenerTotalDescuento,
    calcularIVA,
    obtenerTotalConIVA,
    eliminarLinea,
    obtenerCantidadTotal
  };

  return (
    <CarritoContext.Provider value={value}>
      {children}
    </CarritoContext.Provider>
  );
};
